import React, { useMemo } from "react";

import type { POI, School } from "@local-logic/client";

import { PopupContent } from "../PopupContent";
import { Address } from "./Address";
import { Link } from "./Link";
import { SchoolAttributes } from "./SchoolAttributes";

import * as styles from "./styles";
import { CategoryKey } from "./SchoolAttributes/types";
import { TransitRoutes } from "~/components/TransitRoutes";

export function POIPopupContent({
  marker,
  poiCategory,
  onLocationLinkClick,
  onSchoolLinkClick,
  onClose,
  children,
  showSchoolRatings = false,
}: {
  marker?: POI | School | null;
  poiCategory?: string | null;
  onLocationLinkClick?: (url: string) => void;
  onSchoolLinkClick?: (url: string) => void;
  onClose?: () => void;
  children?: React.ReactNode;
  showSchoolRatings?: boolean;
}) {
  if (!marker) {
    return null;
  }

  const categories = useMemo(() => {
    const isSchool = (item: POI | School): item is School => {
      return "school_attributes" in item;
    };

    if (!isSchool(marker)) {
      return undefined;
    }

    return [
      marker.is_advanced_placement && "advancedPlacement",
      marker.is_charter && "charter",
      marker.is_magnet && "magnet",
    ].filter(Boolean) as CategoryKey[];
  }, [marker, poiCategory]);

  const rating = useMemo(() => {
    if (showSchoolRatings && "rating" in marker && marker.rating) {
      return marker.rating;
    }
    return undefined;
  }, [marker, showSchoolRatings]);

  return (
    <PopupContent title={marker.name} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.content}>
          <Address address={marker.address} />
          <Link url={marker.url} onClick={onLocationLinkClick} />

          <TransitRoutes
            keyPrefix="map-popup-transit-routes"
            routes={(marker as POI).routes}
            useLongName={poiCategory === "train_stations"}
          />
        </div>

        <SchoolAttributes
          attributes={(marker as School).school_attributes}
          boardAttributes={(marker as School).school_board_attributes}
          categories={categories as CategoryKey[] | undefined}
          onLinkClick={onSchoolLinkClick}
          rating={rating}
        />

        {children}
      </div>
    </PopupContent>
  );
}
